<template>
    <div class="h-full w-full flex flex-col">
        <div class="h-full container mx-auto">
            <div class="px-8 flex flex-col h-full w-full py-8">
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/employment" class="opacity-50 hover:opacity-100">Employment</router-link>
                        <span class="mx-2">/</span>
                        <router-link v-if="this.role == 'user'" to="/employment/job-opportunities"
                            class="opacity-50 hover:opacity-100">Job
                            Opportunities</router-link>
                        <router-link v-else to="/jobs" class="opacity-50 hover:opacity-100">Jobs</router-link>
                        <div v-if="!$apollo.queries.jobOpportunity.loading && hasItem">
                            <span class="mx-2">/</span>
                            <span>{{ item.job_title }}</span>
                        </div>
                    </div>
                    <div v-if="!$apollo.queries.jobOpportunity.loading && hasItem" class="font-bold">{{ getEmployerName
                    }}</div>
                </div>

                <div v-if="$apollo.queries.jobOpportunity.loading">
                    <div class="h-full flex justify-center items-center">
                        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                    </div>
                </div>
                <div v-else-if="!$apollo.queries.jobOpportunity.loading && !hasItem"
                    class="h-full flex justify-center items-center">
                    <h1 class="text-center text-xl font-medium">No job opportunity found!</h1>
                </div>
                <div v-else class="w-full mt-8">
                    <!-- Header -->
                    <div class="bg-white shadow-lg border rounded-2xl p-6 mb-6">
                        <div class="flex items-center justify-between flex-wrap gap-4">
                            <div class="flex-1">
                                <div class="flex items-center gap-3 flex-wrap">
                                    <h1 class="text-3xl font-bold text-gray-800">{{ item.job_title }} - {{
                                        getEmployerName }}
                                    </h1>
                                    <span v-if="item.opportunity_type" :class="getOpportunityTypeBadgeClass"
                                        class="px-3 py-1 rounded-full text-sm font-semibold">
                                        {{ getOpportunityTypeLabel }}
                                    </span>
                                </div>
                                <p class="text-sm text-gray-500 mt-1">Posted on {{ formatDate(item.posted_date) }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Job Details Section -->
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <!-- Job Description -->
                        <div v-if="item.job_description || item.minimum_qualification || item.preferred_qualification || item.expected_skills || item.special_instructions || item.benefits"
                            class="lg:col-span-2 bg-white shadow-lg border rounded-2xl p-6">
                            <div v-if="item.job_description" class="mb-6">
                                <h2 class="text-2xl font-semibold text-gray-800 mb-4">Job
                                    Description
                                </h2>
                                <p class="text-gray-600 leading-relaxed">{{
                                    item.job_description
                                }}
                                </p>
                            </div>
                            <div v-if="item.minimum_qualification" class="mb-6">
                                <h3 class="text-xl font-semibold text-gray-800 mb-2">Minimum Qualification</h3>
                                <ul class="list-disc pl-5 space-y-2 text-gray-600">
                                    <li>{{ item.minimum_qualification }}</li>
                                </ul>
                            </div>
                            <div v-if="item.preferred_qualification" class="mb-6">
                                <h3 class="text-xl font-semibold text-gray-800 mb-2">Preferred Qualification</h3>
                                <ul class="list-disc pl-5 space-y-2 text-gray-600">
                                    <li>{{ item.preferred_qualification }}</li>
                                </ul>
                            </div>
                            <div v-if="item.expected_skills" class="mb-6">
                                <h3 class="text-xl font-semibold text-gray-800 mb-2">Expected Skills</h3>
                                <p class="text-gray-600 leading-relaxed">{{
                                    item.expected_skills
                                }}
                                </p>
                            </div>
                            <div v-if="item.special_instructions" class="mb-6">
                                <h3 class="text-xl font-semibold text-gray-800 mb-2">Special Instructions</h3>
                                <p class="text-gray-600 leading-relaxed">{{
                                    item.special_instructions
                                }}
                                </p>
                            </div>
                            <div v-if="item.benefits">
                                <h3 class="text-xl font-semibold text-gray-800 mb-2">Benefits</h3>
                                <p class="text-gray-600 leading-relaxed">{{
                                    item.benefits
                                }}
                                </p>
                            </div>
                        </div>

                        <!-- Job Sidebar -->
                        <aside
                            class="bg-white shadow-xl border border-gray-200 rounded-2xl p-6 flex flex-col space-y-6">
                            <div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-4">Job Overview</h2>
                                <div class="space-y-3">
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🧾 Position:</span>
                                        <span class="text-gray-900 font-semibold">{{ item.position_name }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">📍 Location:</span>
                                        <span class="text-gray-900">{{ item.job_location }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">📄 Contract Type:</span>
                                        <span class="text-gray-900">{{ item.contract_type }}</span>
                                    </div>
                                    <div v-if="role != 'user'" class="flex justify-between">
                                        <span class="text-gray-600 font-medium">💰 Salary:</span>
                                        <span class="text-green-700 font-semibold">{{ item.salary }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">👥 Vacancy:</span>
                                        <span class="text-gray-900">{{ item.vacancy }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">⏰ Working Hours:</span>
                                        <span class="text-gray-900">{{ item.working_hours }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🏢 Division:</span>
                                        <span class="text-gray-900">{{ item.division }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">📌 Department:</span>
                                        <span class="text-gray-900">{{ item.department }}</span>
                                    </div>
                                </div>
                            </div>
                            <hr class="border-t my-2" />
                            <div>
                                <h2 class="text-xl font-semibold text-gray-800 mb-4">Target Audience</h2>
                                <div class="space-y-3">
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🏛️ Entity:</span>
                                        <span class="text-gray-900">{{ getEntityName }}</span>
                                    </div>
                                    <div v-if="item.campus && item.campus.length > 0" class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🏫 Campus:</span>
                                        <span class="text-gray-900">{{ item.campus.join(', ') }}</span>
                                    </div>
                                    <div v-if="item.year_of_study && item.year_of_study.length > 0"
                                        class="flex justify-between">
                                        <span class="text-gray-600 font-medium">
                                            📚 {{ item.entity == 'ATHS' ? 'Grade' : 'Year of Study' }}:
                                        </span>
                                        <span class="text-gray-900">{{ item.year_of_study.join(', ') }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">📘 Program:</span>
                                        <span class="text-gray-900">{{ item.program ? item.program : 'All' }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🎓 Student Status:</span>
                                        <span class="text-gray-900">{{ item.student_status }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🌍 Nationality:</span>
                                        <span class="text-gray-900">{{ getJobFor }}</span>
                                    </div>
                                    <div class="flex justify-between">
                                        <span class="text-gray-600 font-medium">🚻 Gender:</span>
                                        <span class="text-gray-900">{{ getGender }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow flex items-end">
                                <button v-if="isApply" @click.stop="applyJob"
                                    class="w-full bg-blue-600 hover:bg-blue-700 transition-colors duration-200 text-white font-semibold py-3 rounded-xl shadow-md mt-6">
                                    Apply Now
                                </button>
                            </div>
                        </aside>
                    </div>

                    <!-- Company Information Section -->
                    <section v-if="item.partner && item.partner.commercial_activity"
                        class="bg-white shadow-lg border rounded-2xl p-6 mt-6">
                        <h2 class="text-2xl font-semibold text-gray-800 mb-4">About the Company</h2>
                        <p class="text-gray-600 leading-relaxed whitespace-pre-wrap">{{ stripHtml(item.partner.commercial_activity) }}</p>
                    </section>
                </div>

            </div>
        </div>
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
export default {
    name: "job-opportunity",
    props: ["token"],
    data() {
        return {
            isLoading: false,
            error: null,
            success: null,
        };
    },
    watch: {
        jobOpportunity(newVal) {
            if (newVal.error) {
                if (newVal.error_code == 403) {
                    this.$router.replace('/no-permission');
                } else if (newVal.error_code == 401) {
                    this.$router.replace('/');
                } else if (newVal.error_code == 404) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: newVal.message,
                    }).then((result) => {
                        if (result.value || result.dismiss) {
                            this.$router.replace('/dashboard');
                        }
                    });
                } else {
                    alert(newVal.message);
                    this.$router.replace('/');
                }
            }
        },
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        item() {
            return this.jobOpportunity.data;
        },
        hasItem() {
            if (this.item) {
                return true;
            } else {
                return false;
            }
        },
        getEmployerName() {
            if (this.hasItem) {
                if (this.item.partner) {
                    return this.item.partner.en_company_name;
                } else {
                    return this.item.employer_name;
                }
            } else {
                return "";
            }
        },
        isApply() {
            if (this.role == 'user') {
                return true;
            } else {
                return false;
            }
        },
        getEntityName() {
            return this.item.entity.replace(/ATHS/g, "ATS");
        },
        getJobFor() {
            if (this.item.job_for == 1) {
                return "Local";
            } else if (this.item.job_for == 2) {
                return "Non Local";
            } else if (this.item.job_for == 3) {
                return "Both Local and Non Local";
            } else {
                return "";
            }
        },
        getGender() {
            if (this.item.gender) {
                if (this.item.gender == 'M') {
                    return 'Male';
                } else {
                    return 'Female';
                }
            } else {
                return "Both Male & Female";
            }
        },
        studentId() {
            const value = this.$store.getters.student_id;
            if (value === null || value === 'null' || value === undefined || value === '') {
                return null;
            } else {
                return value;
            }
        },
        getOpportunityTypeLabel() {
            if (this.hasItem && this.item.opportunity_type) {
                return this.item.opportunity_type === 'Sponsorship' ? '🎓 Sponsorship' : '💼 Job';
            }
            return '';
        },
        getOpportunityTypeBadgeClass() {
            if (this.hasItem && this.item.opportunity_type) {
                if (this.item.opportunity_type === 'Sponsorship') {
                    return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
                } else {
                    return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
                }
            }
            return '';
        },
    },
    apollo: {
        jobOpportunity() {
            return {
                query: gql`query GetJobOpportunity($token: String!, $student_id: String) {
                    jobOpportunity(token: $token, student_id: $student_id) {
                        error
                        error_code
                        message
                        data {
                            id
                            grade_number
                            job_title
                            position_name
                            position_number
                            job_description
                            minimum_qualification
                            preferred_qualification
                            expected_skills
                            special_instructions
                            division
                            department
                            job_start_date
                            job_close_date
                            job_category
                            salary
                            vacancy
                            approved_count
                            rejected_count
                            days_ago
                            benefits
                            job_location
                            working_hours
                            contract_type
                            entity
                            gender
                            job_for
                            program
                            student_status
                            partner {
                                en_company_name
                                company_short_name
                                commercial_activity
                            }
                            employer_name
                            posted_date
                            campus
                            year_of_study
                            opportunity_type
                        }
                    }
                }`,
                variables() {
                    return {
                        token: this.token ? this.token : "",
                        student_id: this.studentId ? this.studentId : "",
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        stripHtml(html) {
            if (!html) return '';
            const div = document.createElement('div');
            div.innerHTML = html;
            return div.textContent || div.innerText || '';
        },
        formatDate(dateString) {
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            return new Date(dateString).toLocaleDateString('en-US', options);
        },
        async applyJob() {
            if (this.role == 'user') {
                this.isLoading = true;

                const CREATE_MUTATION = gql`
                    mutation ApplyJob($job_id: Int!) {
                        applyJob(job_id: $job_id) {
                            error
                            error_code
                            message
                        }
                    }
                `;

                let responseData;

                try {
                    await this.$apollo.mutate({
                        mutation: CREATE_MUTATION,
                        variables: {
                            job_id: parseInt(this.item.id),
                        }
                    }).then((data) => {
                        responseData = data.data.applyJob;
                        if (data.data.applyJob.error) {
                            this.error = data.data.applyJob.message;
                        } else {
                            this.success = data.data.applyJob.message;
                        }
                    }).catch((error) => {
                        this.error = error.message;
                    })

                } catch (error) {
                    this.error = error.message || "Please try again later.";
                }

                if (this.error === null) {

                    const Toast = this.$swal.mixin({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 1000,
                        timerProgressBar: true,
                        didOpen: (toast) => {
                            toast.addEventListener('mouseenter', this.$swal.stopTimer)
                            toast.addEventListener('mouseleave', this.$swal.resumeTimer)
                        }
                    })
                    Toast.fire({
                        icon: 'success',
                        title: this.success
                    })

                } else {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: this.error,
                    }).then((result) => {
                        if (result.value || result.dismiss) {
                            if (responseData.error_code == 405) {
                                window.location.reload();
                            }
                        }
                    })
                }

                this.isLoading = false;
            }
        },
    },
}
</script>